import { translate } from '@vimeo/i18n-runtime';
import type { SubscriptionPlans } from '@vimeo/subscription-api-plans-data-hook';

export type ComparisonFeatureKey =
  | 'seats'
  | 'storage'
  | 'screen_recording'
  | 'virtual_events'
  | 'bandwidth';
type ComparisonValue = string | boolean;
type ComparisonTier = SubscriptionPlans['tier'];
type TierValueMap = Partial<Record<SubscriptionPlans['tier'], ComparisonValue>>;
type TierSubtitleMap = Partial<Record<SubscriptionPlans['tier'], string>>;
type CardFeatureTier = 'free' | 'starter' | 'standard' | 'advanced';

export interface ComparisonFeatureRow {
  key: ComparisonFeatureKey;
  name: string;
  tooltip?: string;
  currentValue: ComparisonValue;
  targetValue: ComparisonValue;
}

export interface CardEntitlement {
  text: string;
}

type ComparisonFeaturesArgs = {
  currentPlan?: SubscriptionPlans;
  targetPlan?: SubscriptionPlans;
  currentTier?: ComparisonTier;
  targetTier?: ComparisonTier;
};

const SCREEN_RECORDING_VALUES: TierValueMap = {
  free: translate('2k only'),
  starter: translate('Unlimited'),
  standard: translate('Unlimited'),
  advanced: translate('Unlimited'),
};

const VIRTUAL_EVENTS_VALUES: TierValueMap = {
  free: false,
  starter: false,
  standard: false,
  advanced: translate('Unlimited'),
};

const TIER_SUBTITLES: TierSubtitleMap = {
  free: translate('Try Vimeo for free'),
  starter: translate('Host and share videos with ease'),
  standard: translate('Elevate your brand with custom video experiences'),
  advanced: translate('Drive engagement with Vimeo AI and events'),
};

// Source: frontend/features/upgrade-plan/src/constants/planCardsData.tsx (CARD_FEATURE_LIST_MAP)
// Keep in sync with upgrade-plan; this local copy avoids cross-package coupling from paywalls.
const CARD_FEATURE_LIST_BY_TIER: Record<CardFeatureTier, string[]> = {
  free: [
    translate('Creation and editing'),
    translate('Screen recording'),
    translate('Sharing and embedding'),
  ],
  starter: [
    translate('Customizable video player'),
    translate('Password privacy & unlisted links'),
    translate('Review & collaboration tools'),
    translate('Transfer video files'),
  ],
  standard: [
    translate('Branding in the player'),
    translate('Third party player support'),
    translate('Custom watermark'),
    translate('Branded video galleries'),
  ],
  advanced: [
    translate('Hosted livestreamed events'),
    translate('Events Q&A, polls, and chat'),
    translate('Stream to multiple destinations'),
    translate('DVR streaming'),
  ],
};

export function getSubtitleForTier(tier?: ComparisonTier): string {
  if (!tier) {
    return '';
  }

  return TIER_SUBTITLES[tier] ?? '';
}

export function getCardEntitlementsForTier(tier?: ComparisonTier): CardEntitlement[] {
  const featureList =
    tier && (tier === 'free' || tier === 'starter' || tier === 'standard' || tier === 'advanced')
      ? CARD_FEATURE_LIST_BY_TIER[tier]
      : [];

  const entries = featureList.slice(0, 4).map((text) => ({ text }));
  while (entries.length < 4) {
    entries.push({ text: '' });
  }

  return entries;
}

export function getComparisonFeatures({
  currentPlan,
  targetPlan,
  currentTier,
  targetTier,
}: ComparisonFeaturesArgs): ComparisonFeatureRow[] {
  return [
    {
      key: 'seats',
      name: translate('Seats'),
      tooltip: translate(
        'The number of users included in your plan that can upload, manage, and share videos.',
      ),
      currentValue: getSeatsValue(currentPlan),
      targetValue: getSeatsValue(targetPlan),
    },
    {
      key: 'storage',
      name: translate('Storage'),
      tooltip: translate('The total amount of video storage available on your plan.'),
      currentValue: getStorageValue(currentPlan),
      targetValue: getStorageValue(targetPlan),
    },
    {
      key: 'screen_recording',
      name: translate('Screen recording'),
      tooltip: translate('Record your screen directly from Vimeo and share instantly.'),
      currentValue: getMappedValue(SCREEN_RECORDING_VALUES, currentTier),
      targetValue: getMappedValue(SCREEN_RECORDING_VALUES, targetTier),
    },
    {
      key: 'virtual_events',
      name: translate('Virtual events'),
      tooltip: translate(
        'Host live-streamed events with interactive tools like Q&A, polls, and chat.',
      ),
      currentValue: getMappedValue(VIRTUAL_EVENTS_VALUES, currentTier),
      targetValue: getMappedValue(VIRTUAL_EVENTS_VALUES, targetTier),
    },
    {
      key: 'bandwidth',
      name: translate('Bandwidth'),
      tooltip: translate('The amount of data that can be streamed to viewers each month.'),
      currentValue: getBandwidthValue(currentPlan),
      targetValue: getBandwidthValue(targetPlan),
    },
  ];
}

export function getFeatureValue(
  features: ComparisonFeatureRow[],
  key: ComparisonFeatureKey,
  source: 'currentValue' | 'targetValue',
): ComparisonValue {
  return features.find((feature) => feature.key === key)?.[source] ?? '';
}

export function getSeatCountLabel(value: ComparisonValue): string {
  if (typeof value !== 'string') {
    return '';
  }

  const seatCount = Number(value);
  if (!Number.isFinite(seatCount) || seatCount <= 0) {
    return value;
  }

  return translate({
    singular: '{COUNT} user',
    plural: '{COUNT} users',
    count: seatCount,
    replacements: { COUNT: seatCount },
  });
}

export function getStorageLabel(value: ComparisonValue): string {
  return typeof value === 'string' ? value : '';
}

function getSeatsValue(plan?: SubscriptionPlans): string {
  const rawSeats = plan?.metadata?.entitlements?.params?.teamSeats;
  if (typeof rawSeats === 'number' && rawSeats > 0) {
    return String(rawSeats);
  }
  return '';
}

function getStorageValue(plan?: SubscriptionPlans): string {
  const params = plan?.metadata?.entitlements?.params;
  const resolvedValue = params?.restrictedVideoStorageLimit ?? params?.videoStoragePeriodicQuota;
  return resolvedValue ?? '';
}

function getBandwidthValue(plan?: SubscriptionPlans): string {
  const bandwidth = plan?.metadata?.entitlements?.params?.bandwidth;
  if (bandwidth?.periodicQuota) {
    let quotaPeriod = translate('month');
    if (bandwidth.quotaPeriod === 'year') {
      quotaPeriod = translate('year');
    }
    return translate({
      singular: '{BANDWIDTH} per {PERIOD}',
      replacements: {
        BANDWIDTH: bandwidth.periodicQuota,
        PERIOD: quotaPeriod,
      },
    });
  }
  return '';
}

function getMappedValue(mapping: TierValueMap, tier: ComparisonTier | undefined): ComparisonValue {
  return getTierFallback(mapping, tier) ?? '';
}

function getTierFallback(
  mapping: TierValueMap,
  tier?: ComparisonTier,
): ComparisonValue | undefined {
  if (!tier) {
    return undefined;
  }

  return mapping[tier];
}
